import classNames from 'classnames';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { Chart } from '../Chart/ChartRoot/Chart';
import { ResizeHandle } from '../ContentGrid/ResizeHandle';
import { DEFAULT_GRID_ROWS } from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import styles from './resizable-split-pane.module.css';
import { SplitPaneProvider } from './SplitPaneContext';

const STORAGE_KEY_PREFIX = 'custom-dash-split-';

const getStorageKey = (pageId: string) => `${STORAGE_KEY_PREFIX}${pageId}`;

const loadSavedSizes = (pageId: string) => {
  const saved = localStorage.getItem(getStorageKey(pageId));
  if (!saved) return null;

  const parsed = JSON.parse(saved);
  return parsed;
};

const saveSizes = (
  pageId: string,
  leftWidth: number,
  isLeftCollapsed: boolean,
  isRightCollapsed: boolean
) => {
  localStorage.setItem(
    getStorageKey(pageId),
    JSON.stringify({
      leftWidth,
      isLeftCollapsed,
      isRightCollapsed,
    })
  );
};

export const ResizableSplitPane = ({ items }: { items: PositionedItem[] }) => {
  const leftItem = items?.[0];
  const rightItem = items?.[1];

  const additionalItems = items.slice(2) ?? [];

  const { config } = useTabContentConfig();
  const pageId = config.id;

  const effectiveFilters = useEffectiveFilters();
  const hasVisibleFilters = effectiveFilters.some((filter) => filter.isVisible);

  const saved = loadSavedSizes(pageId);

  const [leftWidth, setLeftWidth] = useState<number>(saved?.leftWidth ?? 50);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(
    saved?.isLeftCollapsed ?? false
  );
  const [isRightCollapsed, setIsRightCollapsed] = useState(
    saved?.isRightCollapsed ?? false
  );

  useEffect(() => {
    saveSizes(pageId, leftWidth, isLeftCollapsed, isRightCollapsed);
  }, [leftWidth, isLeftCollapsed, isRightCollapsed, pageId]);

  const handleResize = useCallback((newLeftWidth: number) => {
    const threshold = 5;

    if (newLeftWidth < threshold) {
      setIsLeftCollapsed(true);
      setIsRightCollapsed(false);
      setLeftWidth(50);
    } else if (newLeftWidth > 100 - threshold) {
      setIsRightCollapsed(true);
      setIsLeftCollapsed(false);
      setLeftWidth(50);
    } else {
      setLeftWidth(newLeftWidth);
      setIsLeftCollapsed(false);
      setIsRightCollapsed(false);
    }
  }, []);

  const handleCollapseLeft = useCallback(() => {
    if (isLeftCollapsed) {
      setIsLeftCollapsed(false);
      setIsRightCollapsed(false);
      setLeftWidth(50);
    } else {
      setIsLeftCollapsed(true);
      setIsRightCollapsed(false);
    }
  }, [isLeftCollapsed]);

  const handleCollapseRight = useCallback(() => {
    if (isRightCollapsed) {
      setIsLeftCollapsed(false);
      setIsRightCollapsed(false);
      setLeftWidth(50);
    } else {
      setIsRightCollapsed(true);
      setIsLeftCollapsed(false);
    }
  }, [isRightCollapsed]);

  // Clicking a results row opens a detail popout anchored to the row. When the
  // right pane is collapsed the table is full-width and the popout gets clipped,
  // so force the right pane back open to give the popout room.
  const expandRightPane = useCallback(() => {
    if (!isRightCollapsed) return;
    setIsLeftCollapsed(false);
    setIsRightCollapsed(false);
    // Functional update so this callback (and thus the context value) stays
    // stable across resize drags — reading leftWidth as a dep would rebuild it
    // on every drag tick and re-render every popout anchor consuming the context.
    setLeftWidth((width) => (width < 5 || width > 95 ? 50 : width));
  }, [isRightCollapsed]);

  const splitPaneContextValue = useMemo(
    () => ({ expandRightPane }),
    [expandRightPane]
  );

  const gridTemplateColumns = (() => {
    if (isLeftCollapsed) {
      return '0fr 8px 1fr';
    }
    if (isRightCollapsed) {
      return '1fr 8px 0fr';
    }
    const rightWidth = 100 - leftWidth;
    return `${leftWidth}fr 8px ${rightWidth}fr`;
  })();

  if (!hasVisibleFilters) {
    return (
      <div className={styles.singlePane}>
        {rightItem && <Chart item={rightItem} />}
      </div>
    );
  }

  const rightItemRow = rightItem?.position?.y ? rightItem.position.y + 1 : 1;
  const rightItemRowSpan = rightItem?.position?.height
    ? rightItem.position.height
    : DEFAULT_GRID_ROWS;
  const leftItemRow = leftItem?.position?.y ? leftItem.position.y + 1 : 1;
  const leftItemRowSpan = leftItem?.position?.height
    ? leftItem.position.height
    : DEFAULT_GRID_ROWS;

  return (
    <SplitPaneProvider value={splitPaneContextValue}>
      <div
        className={styles.splitPane}
        data-collapsed={isLeftCollapsed || isRightCollapsed}
        style={
          {
            '--split-rows': DEFAULT_GRID_ROWS,
            '--split-columns': gridTemplateColumns,
          } as CSSProperties
        }
      >
        <div
          className={classNames(styles.pane, styles.leftPane)}
          style={
            {
              '--pane-row': `${leftItemRow} / span ${leftItemRowSpan}`,
            } as CSSProperties
          }
        >
          {!isLeftCollapsed && leftItem && <Chart item={leftItem} />}
        </div>

        <ResizeHandle
          onResize={handleResize}
          initialLeftWidth={leftWidth}
          onCollapseLeft={handleCollapseLeft}
          onCollapseRight={handleCollapseRight}
          isLeftCollapsed={isLeftCollapsed}
          isRightCollapsed={isRightCollapsed}
        />

        <div
          className={classNames(styles.pane, styles.rightPane)}
          style={
            {
              '--pane-row': `${rightItemRow} / span ${rightItemRowSpan}`,
            } as CSSProperties
          }
        >
          {!isRightCollapsed && rightItem && <Chart item={rightItem} />}
        </div>
        {additionalItems.map((item) => (
          <div
            key={item.id}
            className={classNames(styles.pane, styles.extraPane)}
            style={
              {
                '--pane-row': `${item.position.y + 1} / span ${item.position.height}`,
              } as CSSProperties
            }
          >
            <Chart item={item} />
          </div>
        ))}
      </div>
    </SplitPaneProvider>
  );
};
